import { useState } from 'react'
import { Alert, Button, Form, Input, Modal, Space, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CheckCircleOutlined,
  EyeOutlined,
  HistoryOutlined,
  PlusOutlined,
  SwapOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePlanDisplayIndex } from '@/shared/api/reference'
// `Can` bu yerda ISHLATILMAYDI: u `canAny` (YOKI) semantikasida, bu
// sahifada esa IKKI huquq BIRGA kerak (`canAll`) — sabab pastda.
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { AgentRef } from '@/features/agent/AgentRef'
import { VoucherActivateModal } from '@/features/voucher/VoucherActivateModal'
import { VoucherBatchFormModal } from '@/features/voucher/VoucherBatchFormModal'
import { VoucherBatchNetworkFilter } from '@/features/voucher/VoucherBatchNetworkFilter'
import { VoucherCardLookup } from '@/features/voucher/VoucherCardLookup'
import { VoucherCodesDrawer } from '@/features/voucher/VoucherCodesDrawer'
import { VoucherRangeVoidModal } from '@/features/voucher/VoucherRangeVoidModal'
import { VoucherTransferModal } from '@/features/voucher/VoucherTransferModal'
import { useVoucherHolders } from '@/features/voucher/useVoucherHolders'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  expiredLossMinor,
  fetchVoucherBatchPage,
  voidableCount,
  voidVoucherBatch,
  VOUCHER_REASON_MAX,
  VOUCHER_STATES,
  type VoucherBatch,
  type VoucherState,
} from '@/features/voucher/voucherApi'

/**
 * Karta holatining rangi — MA'NOSI bo'yicha:
 *   · NEW       — ko'k, hali sotilmagan (ombordagi qiymat)
 *   · SOLD      — sariq, pul olingan, lekin xizmat hali berilmagan
 *   · ACTIVATED — yashil, ishlatildi
 *   · EXPIRED   — kulrang, sotilmasdan kuydi
 *   · VOIDED    — qizil, bekor qilingan (yo'qolgan/o'g'irlangan partiya)
 */
const VOUCHER_STATE_COLOR: Record<VoucherState, string> = {
  NEW: 'blue',
  SOLD: 'gold',
  ACTIVATED: 'green',
  EXPIRED: 'default',
  VOIDED: 'red',
}

/**
 * Vaucherlar — 2-yo'nalish (mehmonxona/kafe) uchun bir marta chop etiladigan
 * kartalar. Bu sahifa faqat ADMIN tomoni: partiya yaratish, ro'yxat, bekor
 * qilish (`VoucherController` javadoc'i — aktivlashtirish "Guest" zonasiga
 * tegishli, portal moduli ajratilganda o'sha yerga ko'chadi, hozircha bu
 * yerda umuman YO'Q — admin panelda kerak emas).
 *
 * ⚠️ SAHIFALASH, QIDIRUV VA SARALASH — SERVERDA (2026-08-20).
 * `GET /api/vouchers/batches` dan `limit` parametri OLIB TASHLANDI va
 * o'rniga `page`/`size`/`search`/`sortField`/`sortOrder` keldi. Shu bilan
 * birga «ro'yxat 100 tada kesildi» banneri ham ketdi: kesish endi yo'q,
 * sahifa tugmalari bor.
 *
 * ⚠️ MUDDAT BO'YICHA FILTR («faqat amaldagilar / faqat kuyganlar»)
 * OLIB TASHLANDI: serverda bunday filtr yo'q va sahifalangan ro'yxatda
 * uni mijozda qilish faqat joriy sahifani toraytirardi. O'rniga «Amal
 * qilish muddati» ustuni SARALANADI (`VALID_UNTIL`) — u butun ro'yxatga
 * qo'llanadi va «qaysi partiya birinchi kuyadi» degan savolga aynan
 * javob beradi.
 */
export function VouchersPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [activateOpen, setActivateOpen] = useState(false)
  const [codesBatch, setCodesBatch] = useState<VoucherBatch | null>(null)
  const [voidTarget, setVoidTarget] = useState<VoucherBatch | null>(null)
  const [transferTarget, setTransferTarget] = useState<VoucherBatch | null>(null)
  const [rangeTarget, setRangeTarget] = useState<VoucherBatch | null>(null)
  /** Oxirgi amal natijasi — bekor qilishda HAQIQATDA kuygan kartalar soni bilan */
  const [notice, setNotice] = useState<string | null>(null)
  const [reasonForm] = Form.useForm<{ reason?: string }>()

  const { can, canAll } = usePermissions()
  // ⚠️ `plan.read` YO'Q rol (AGENT, VENUE_MANAGER) vaucherlarni ishlatadi,
  // lekin katalogni o'qiy olmaydi — so'rov gate qilinmasa har ochilishda
  // 403 berardi. Ustun `?? row.planId` bilan id ko'rsatadi, ko'rinish bir xil.
  const planNames = usePlanDisplayIndex(can('plan.read'))
  /*
    ⚠️ Daraxt "Ushlovchi" USTUNI uchun kerak, oyna uchun emas — ya'ni
    sahifa ochilishi bilan so'raladi. Kesh kaliti Tashkiliy tuzilma
    sahifasiniki bilan BITTA (`queryKeys.org.tree`), shuning uchun bu
    qo'shimcha so'rov emas.

    ⚠️ Huquq yo'q bo'lsa so'rov UMUMAN ketmaydi (`useVoucherHolders`
    ichida) — ustun o'shanda "nomi noma'lum" deb yozadi, bo'sh emas.
  */
  const holders = useVoucherHolders()

  /*
    ⚠️ IKKI HUQUQ BIRGA (`canAll`, `canAny` EMAS) — backend bilan AYNAN
    bir xil shart (`VoucherController.generate`).

    Ilgari bu yerda faqat `subscriber.create` tekshirilardi va u
    `RESELLER` rolida ham bor: reseller o'ziga karta partiyasi chiqarib
    olardi — narxi tenant hisobiga, kartalar esa uning qo'lida
    (X-SIB-010). Ikkinchi huquq `plan.create`, ya'ni "nima sotilishini
    belgilash" darajasi.

    ⚠️ Tugmani yashirish HIMOYA EMAS (04-ROLES.md §8) — u faqat bosib
    bo'lmaydigan tugmani ko'rsatmaslik uchun. Haqiqiy to'siq backendda,
    va u ALOHIDA tuzatildi; bu yerdagisi usiz ma'nosiz bo'lardi.
  */
  const canMintBatch = canAll('subscriber.create', 'plan.create')
  const canVoidBatch = canAll('subscriber.delete', 'plan.create')
  /*
    ⚠️ ISHLATISH — CHIQARISHDAN PAST shart, va bu ataylab.

    `VoucherController.activate` `voucher.activate` talab qiladi —
    kartani ishlatish "nima sotilishini belgilash" emas, "sotilganini
    yetkazish".

    ⚠️ Ilgari bu shart `subscriber.create` edi. Egasi 2026-08-16 da
    «AGENT ham ishlata olsin» degan (BUG-009), va agentga
    `subscriber.create` berish uni KARTASIZ ham, ixtiyoriy tarif bilan
    abonent ocha oladigan qilib qo'yardi — so'ralganidan ancha keng.
    Shuning uchun alohida, TOR huquq ajratildi
    (`V202608530001`): u faqat MAVJUD kartani ishlatishga ruxsat
    beradi, tarif esa kartaning o'zida yozilgan.

    Huquqni MODERATOR, RESELLER va AGENT oldi; SUPERADMIN va ADMIN uni
    umumiy ro'yxatdan oladi. Ya'ni ilgari ishlata olgan hech kim
    yo'lni yo'qotmadi — migratsiyaning 3-bo'limi buni tekshiradi.
  */
  const canActivate = canAll('voucher.activate')

  /*
    ⚠️ XATO OYNANING ICHIDA (`VoucherVoidModal` naqshi), sahifa
    banneriga EMAS: ilgari u banner'ga yozilardi, oyna esa OCHIQ qolardi
    — xabar modal niqobi ortida turardi va operator faqat spinner
    to'xtaganini ko'rardi.

    ⚠️ JAVOBDAGI `voided` ENDI TASHLANMAYDI. Server nechta karta
    HAQIQATDA kuyganini aytadi va bu son partiya HAJMIDAN kam bo'lishi
    mumkin: aktivlashtirilgan va kuygan kartalar bekor qilinmaydi
    (`voidVoucher` javadoc'i — ular 409 beradi). Uni tashlab yuborish
    "25 ta karta bekor qilindi" degan yolg'on taassurot qoldirardi,
    holbuki ulardan 7 tasi allaqachon ishlatilgan bo'lishi mumkin.
  */
  const voidBatch = useMutation({
    mutationFn: (values: { id: string; reason?: string }) => voidVoucherBatch(values.id, values.reason),
    onSuccess: (voided) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.batches })
      setVoidTarget(null)
      reasonForm.resetFields()
      setNotice(t('voucher.batch.voidedResult', { count: voided }))
    },
  })

  const closeVoid = () => {
    voidBatch.reset()
    setVoidTarget(null)
  }

  /** `null` — partiya tanlanmagan yoki sanoq noma'lum (`voidableCount` javadoc'i) */
  const voidable = voidTarget === null ? null : voidableCount(voidTarget)

  const columns: ColumnsType<VoucherBatch> = [
    {
      key: 'batchNo',
      title: t('voucher.batch.batchNo'),
      width: 130,
      sorter: true,
      render: (_, row) => (
        <Space direction="vertical" size={2}>
          <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>
            №{row.batchNo}
          </Typography.Text>
          {row.credentialMode === 'LOGIN_PASSWORD' ? (
            <Tooltip
              title={t('voucher.batch.credentialDigits', { login: row.loginDigits, password: row.passwordDigits })}
            >
              <Tag color="purple">{t('voucher.credentialMode.LOGIN_PASSWORD')}</Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      /*
        ⚠️ SARALANMAYDI: paket NOMI serverda yo'q (javobda faqat
        `planId`), nomni panel katalogdan qo'shadi. Saralash tugmasi
        bu ustunda «nom bo'yicha tartiblandi» degan yolg'on va'da
        bo'lardi.
      */
      key: 'plan',
      title: t('voucher.batch.plan'),
      width: 200,
      render: (_, row) => planNames.get(row.planId) ?? row.planId,
    },
    {
      key: 'note',
      title: t('voucher.batch.note'),
      width: 180,
      ellipsis: true,
      render: (_, row) => (row.note ? <span dir="auto">{row.note}</span> : t('common.dash')),
    },
    {
      key: 'visp',
      title: t('voucher.batch.visp'),
      width: 180,
      ellipsis: true,
      render: (_, row) =>
        row.visp ? (
          <Tooltip title={row.visp.centerName}>
            <span>{row.visp.name}</span>
          </Tooltip>
        ) : (
          t('common.dash')
        ),
    },
    {
      key: 'codePrefix',
      title: t('voucher.batch.codePrefix'),
      width: 120,
      sorter: true,
      render: (_, row) => (row.codePrefix ? <Typography.Text code>{row.codePrefix}</Typography.Text> : '—'),
    },
    {
      key: 'quantity',
      title: t('voucher.batch.quantity'),
      width: 100,
      align: 'right',
      sorter: true,
      render: (_, row) => row.quantity,
    },
    {
      /*
        ⚠️ PARTIYA HAQIDAGI ASOSIY SAVOL — "nechtasi hali sotilmagan".

        Ilgari ustunda faqat `quantity` turardi ("25 ta chiqarildi") va
        holat sanog'ini ko'rishning yagona yo'li "Kodlarni ko'rish"
        oynasini ochish edi — u esa `subscriber.create` huquqini talab
        qiladi va KODLARNING O'ZINI ochadi. Ya'ni oddiy sanoq uchun
        operator sirni ochishga majbur bo'lardi.

        ⚠️ Nol qiymatli holatlar CHIZILMAYDI: beshta holatning hammasini
        har qatorda ko'rsatish ustunni "25 · 0 · 0 · 0 · 0" ga
        aylantirardi va ma'noli raqam shovqin ichida yo'qolardi.
      */
      key: 'usage',
      title: t('voucher.batch.usage'),
      width: 220,
      render: (_, row) => {
        if (!row.counts) return t('common.dash')
        const shown = VOUCHER_STATES.filter((s) => (row.counts?.[s] ?? 0) > 0)
        if (shown.length === 0) return t('common.dash')
        const lost = expiredLossMinor(row)
        return (
          <Space direction="vertical" size={2}>
            <Space size={4} wrap>
              {/* ⚠️ Izoh teg ustida: «Chop etilgan» va «Aktivlashtirilgan»
                  farqi kod bilan tanishmagan odam uchun ekranda HECH
                  QAYERDA yozilmagan edi (tashqi tekshiruvchi topdi). */}
              {shown.map((s) => (
                <Tooltip key={s} title={t(`voucherStateHint.${s}`)}>
                  <Tag color={VOUCHER_STATE_COLOR[s]}>
                    {t(`voucherState.${s}`)}: {row.counts?.[s]}
                  </Tag>
                </Tooltip>
              ))}
            </Space>
            {/*
              ⚠️ ZARAR — SANOQDAN ALOHIDA. "Muddati o'tgan: 40" degan
              raqam pul haqida hech narsa aytmaydi: 40 ta arzon karta
              va 40 ta qimmat karta bir xil ko'rinardi
              (`QARORLAR.md` §20.8 — "moliyaviy zarar").

              ⚠️ Bu KUTILAYOTGAN daromadning yo'qolishi, kassadan
              chiqqan pul EMAS — shuning uchun "xarajat" deb emas,
              "kuygan qiymat" deb yoziladi.
            */}
            {lost !== null && lost > 0 ? (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                {t('voucher.batch.expiredLoss', {
                  value: formatMoneyValue({ amount: lost, currency: row.price.currency }),
                })}
              </Typography.Text>
            ) : null}
          </Space>
        )
      },
    },
    {
      key: 'price',
      title: t('catalog.plan.price'),
      width: 140,
      align: 'right',
      render: (_, row) => formatMoneyValue(row.price),
    },
    {
      /*
        ⚠️ USTUN ENDI "AGENT" EMAS, "USHLOVCHI".

        Partiya ikki xil tomonda bo'lishi mumkin va ular boshqa-boshqa
        savolga javob beradi: agent — "kimga berildi" (komissiya),
        ombor/filial — "qayerda yotibdi". Ilgari faqat birinchisi bor
        edi, ya'ni "qayerda?" degan savol javobsiz qolardi.

        ⚠️ Ikkalasi ham bo'sh bo'lsa "—" EMAS, "operatorniki" deb
        yoziladi. Staging'da 11 partiyadan 9 tasi aynan shunday va
        chiziqcha ularni "ma'lumot yetishmayapti" deb ko'rsatardi —
        aslida bu to'liq ma'noli holat.
      */
      key: 'holder',
      title: t('voucher.batch.holder'),
      width: 170,
      render: (_, row) => {
        if (row.agentId) return <AgentRef agentId={row.agentId} />
        if (!row.nodeId) {
          return <Typography.Text type="secondary">{t('voucher.batch.holderOperator')}</Typography.Text>
        }
        const node = holders.nodes.find((n) => n.id === row.nodeId)
        if (node) return <Tag color="blue">{node.displayName}</Tag>
        // ⚠️ "Nomi noma'lum" va "ushlovchi yo'q" ajratiladi: daraxtni
        // o'qish huquqi yo'q bo'lsa ham partiya BIRIKTIRILGAN, va buni
        // yashirish operatorni "hech qayerda emas" deb chalg'itardi.
        return <Tag>{t('voucher.batch.holderUnknown')}</Tag>
      },
    },
    {
      /*
        ⚠️ Saralash MUDDAT bo'yicha filtrning O'RNINI bosadi (sinf
        javadoc'i): «qaysi partiya birinchi kuyadi» — shu ustunni
        o'suvchi tartibda bosish.
      */
      key: 'validUntil',
      title: t('voucher.batch.validUntil'),
      width: 140,
      sorter: true,
      render: (_, row) => (row.validUntil ? formatDateTime(row.validUntil) : t('limits.noExpiration')),
    },
    {
      key: 'createdAt',
      title: t('audit.columns.at'),
      width: 160,
      sorter: true,
      render: (_, row) => (
        <Typography.Text title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</Typography.Text>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        // ⚠️ Shartlar `canAll` bilan hisoblangan (IKKI huquq BIRGA), ya'ni
        // ular `RowActions.permission` ga (YOKI semantikasi) berilmaydi —
        // `hidden` orqali o'tadi va mantiq aynan saqlanadi.
        <RowActions
          items={[
            {
              /* Kodlarni ko'rish — partiya CHIQARISH bilan bir xil shart:
                 kod pul qiymatiga teng bearer-token, uni ko'rgan odam
                 partiyani ishlatib yubora oladi (`VoucherController.codes`) */
              key: 'viewCodes',
              label: <Tooltip title={t('voucher.viewCodesHint')}>{t('voucher.viewCodes')}</Tooltip>,
              icon: <EyeOutlined />,
              onClick: () => setCodesBatch(row),
              hidden: !canMintBatch,
            },
            {
              /* Ko'chirish — partiya CHIQARISH bilan bir xil huquq
                 (`VoucherController.transferBatch`): u "kim sotadi" ni hal
                 qiladi. ⚠️ Amalni yashirish HIMOYA EMAS (04-ROLES.md §8) */
              key: 'transfer',
              label: t('voucher.batch.transfer'),
              onClick: () => setTransferTarget(row),
              hidden: !canMintBatch,
            },
            {
              key: 'voidRange',
              label: t('voucher.batch.voidRange'),
              onClick: () => setRangeTarget(row),
              danger: true,
              divider: true,
              hidden: !canVoidBatch,
            },
            {
              key: 'void',
              label: t('voucher.batch.void'),
              onClick: () => setVoidTarget(row),
              danger: true,
              hidden: !canVoidBatch,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.vouchers')}
        description={t('pageIntro.vouchers')}
        extra={
          <Space>
            {/*
              ⚠️ "Kartani ishlatish" — ALOHIDA huquq bilan.

              Partiya chiqarish ikki huquqni birga talab qiladi
              (`subscriber.create` + `plan.create`), ishlatish esa faqat
              `subscriber.create` — backendda AYNAN shunday
              (`VoucherController.activate`). Ularni bitta shartga
              qo'shish `RESELLER` ni (unda `plan.create` yo'q) o'zi
              sotgan kartani ishlata olmaydigan holatga qo'yardi,
              holbuki server unga ruxsat beradi.

              ⚠️ Tugmani ko'rsatish/yashirish HIMOYA EMAS (04-ROLES.md
              §8) — haqiqiy to'siq backendda.
            */}
            <Link to="/vouchers/register">
              <Button icon={<UnorderedListOutlined />}>{t('nav.voucherRegister')}</Button>
            </Link>
            <Link to="/vouchers/transfers">
              <Button icon={<SwapOutlined />}>{t('nav.voucherTransfers')}</Button>
            </Link>
            <Link to="/vouchers/activations">
              <Button icon={<HistoryOutlined />}>{t('nav.voucherActivations')}</Button>
            </Link>
            {canActivate ? (
              <Button icon={<CheckCircleOutlined />} onClick={() => setActivateOpen(true)}>
                {t('voucher.activate')}
              </Button>
            ) : null}
            {canMintBatch ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
                {t('voucher.batch.createTitle')}
              </Button>
            ) : null}
          </Space>
        }
      />

      {can('subscriber.read') ? (
        <VoucherCardLookup planName={(planId) => planNames.get(planId) ?? planId} />
      ) : null}

      {/* Natija — HAQIQIY son, partiya hajmi emas */}
      {notice === null ? null : (
        <Alert
          type="success"
          showIcon
          closable
          message={notice}
          onClose={() => setNotice(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/*
        ⚠️ Ro'yxat XATOSI, «qayta urinish» tugmasi va bo'sh holat —
        hammasini `DataTable` o'zi chizadi. Ilgari bu yerda o'z banneri
        va o'z bo'sh holati bor edi; ular endi ikkinchi nusxa bo'lardi.
      */}
      <DataTable<VoucherBatch>
        queryKey={queryKeys.vouchers.batches}
        fetchPage={fetchVoucherBatchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="voucher-batches"
        stickyFirstColumn
        searchable
        searchPlaceholder={t('voucher.batch.searchPlaceholder')}
        {...(can('network.visp.read') ? { toolbarExtra: <VoucherBatchNetworkFilter /> } : {})}
        scrollX={1750}
        emptyVariant="no-data"
        emptyTitle={t('voucher.batch.empty')}
        emptyDescription={t('voucher.batch.emptyHint')}
        {...(canMintBatch
          ? {
              emptyAction: {
                label: t('voucher.batch.createTitle'),
                onClick: () => setCreateOpen(true),
              },
            }
          : {})}
      />

      <VoucherBatchFormModal open={createOpen} onClose={() => setCreateOpen(false)} />

      <VoucherActivateModal open={activateOpen} onClose={() => setActivateOpen(false)} />

      <VoucherCodesDrawer batch={codesBatch} onClose={() => setCodesBatch(null)} />

      <VoucherTransferModal
        batch={transferTarget}
        onClose={() => setTransferTarget(null)}
        onTransferred={(batchNo, transferNo) =>
          setNotice(
            transferNo === null
              ? t('voucher.transfer.doneNoCode', { batchNo })
              : t('voucher.transfer.done', { batchNo, transferNo }),
          )
        }
      />

      <VoucherRangeVoidModal
        batch={rangeTarget}
        onClose={() => setRangeTarget(null)}
        onVoided={(batchNo, result) => setNotice(t('voucher.rangeVoid.result', { batchNo, ...result }))}
      />

      <Modal
        open={voidTarget !== null}
        title={t('voucher.batch.voidTitle')}
        onCancel={closeVoid}
        onOk={() =>
          submitOnValid(reasonForm, (values) => {
            if (!voidTarget) return
            voidBatch.mutate({ id: voidTarget.id, reason: values.reason?.trim() || undefined })
          })
        }
        confirmLoading={voidBatch.isPending}
        okText={t('voucher.batch.void')}
        okButtonProps={{ danger: true }}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {/*
          ⚠️ OGOHLANTIRISH PARTIYA HAJMINI EMAS, BEKOR QILINADIGAN SONNI
          aytadi. Ilgari bu yerda `quantity` turardi ("barcha 25 ta karta
          ishlatib bo'lmaydigan bo'ladi") — holbuki aktivlashtirilgan va
          kuygan kartalarga amal TEGMAYDI. Tasdiq matni haqiqiydan ko'p
          karta va'da qilardi, natija esa umuman ko'rsatilmasdi.
        */}
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('voucher.batch.voidWarningTitle')}
          description={
            voidable === null
              ? t('voucher.batch.voidWarningUnknown')
              : t('voucher.batch.voidWarningCounted', { count: voidable })
          }
        />

        {/* Xato SHU YERDA qoladi — oyna ochiq, amal tugmasi bilan bir ekranda */}
        {voidBatch.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(voidBatch.error, t)}
          />
        ) : null}

        <Form form={reasonForm} layout="vertical" preserve={false} disabled={voidBatch.isPending}>
          <Form.Item name="reason" label={t('actions.reason')} extra={t('actions.reasonHint')}>
            <Input.TextArea rows={2} maxLength={VOUCHER_REASON_MAX} autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
